import type * as Monaco from 'monaco-editor';
import type { CompletionCatalog, PolicyViolation, ValidateQueryResponse } from '@idas/shared';

/**
 * Schema-aware SQL editing: the part that makes this feel like Workbench
 * rather than a textarea.
 *
 * Three things happen here:
 *
 *  1. Completions that know the actual schema, including which table an alias
 *     refers to, so `o.` after `FROM orders o` suggests orders' columns.
 *  2. Restricted columns are surfaced in the completion list itself, marked
 *     and sorted last — you can see that `email` will come back masked before
 *     you write the query, not after.
 *  3. Server-side policy violations become editor markers, so a blocked table
 *     gets a red underline in place rather than an error message elsewhere.
 */

const TABLE_CONTEXT = /\b(from|join|update|into)\s+[\w.`]*$/i;
/** `alias.` or `db.table.` immediately before the cursor. */
const QUALIFIER_CONTEXT = /([\w`]+)\.\s*([\w`]*)$/;

interface AliasBinding {
  alias: string;
  /** Fully qualified `db.table`, or a bare table name to be resolved. */
  target: string;
}

/**
 * Finds table aliases declared anywhere in the statement.
 *
 * Deliberately a regex rather than a parse: the text under an editing cursor
 * is usually mid-edit and un-parseable, and a completion provider that only
 * works on syntactically valid SQL is useless exactly when it is needed.
 */
export function extractAliases(sql: string): AliasBinding[] {
  const bindings: AliasBinding[] = [];
  const pattern =
    /\b(?:from|join)\s+([\w`.]+)(?:\s+(?:as\s+)?(?!on\b|where\b|join\b|inner\b|left\b|right\b|full\b|cross\b|group\b|order\b|limit\b|having\b|using\b|set\b)([a-z_]\w*))?/gi;

  let match: RegExpExecArray | null;
  while ((match = pattern.exec(sql)) !== null) {
    const target = (match[1] ?? '').replace(/`/g, '');
    const alias = match[2];
    if (!target) continue;

    // The table's own name is always a valid qualifier, alias or not.
    const bare = target.includes('.') ? target.split('.').pop()! : target;
    bindings.push({ alias: bare, target });
    if (alias) bindings.push({ alias, target });
  }

  return bindings;
}

function resolveTable(catalog: CompletionCatalog, name: string) {
  const normalized = name.toLowerCase();
  return (
    catalog.tables.find((t) => t.qualified.toLowerCase() === normalized) ??
    catalog.tables.find((t) => t.name.toLowerCase() === normalized)
  );
}

export function registerSqlCompletions(
  monaco: typeof Monaco,
  getCatalog: () => CompletionCatalog | null,
): Monaco.IDisposable {
  return monaco.languages.registerCompletionItemProvider('sql', {
    triggerCharacters: ['.', ' '],

    provideCompletionItems(model, position) {
      const catalog = getCatalog();
      if (!catalog) return { suggestions: [] };

      const word = model.getWordUntilPosition(position);
      const range: Monaco.IRange = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };

      const linePrefix = model.getValueInRange({
        startLineNumber: position.lineNumber,
        startColumn: 1,
        endLineNumber: position.lineNumber,
        endColumn: position.column,
      });
      const fullText = model.getValue();
      const suggestions: Monaco.languages.CompletionItem[] = [];

      // ── `alias.` → that table's columns ─────────────────────────────────
      const qualifier = QUALIFIER_CONTEXT.exec(linePrefix);
      if (qualifier) {
        const qualifierName = (qualifier[1] ?? '').replace(/`/g, '');
        const aliases = extractAliases(fullText);
        const binding = aliases.find(
          (a) => a.alias.toLowerCase() === qualifierName.toLowerCase(),
        );

        const table = resolveTable(catalog, binding?.target ?? qualifierName);
        if (table) {
          table.columns.forEach((column, index) => {
            suggestions.push({
              label: column.name,
              kind: monaco.languages.CompletionItemKind.Field,
              insertText: column.name,
              range,
              detail: column.dataType,
              documentation: column.restriction === 'MASKED'
                ? { value: '**Masked** — values are rewritten before they reach you.' }
                : undefined,
              // Column order, not alphabetical: people look for columns where
              // they sit in the table.
              sortText: String(index).padStart(4, '0'),
              tags: column.restriction === 'MASKED'
                ? [monaco.languages.CompletionItemTag.Deprecated]
                : undefined,
            });
          });
          return { suggestions };
        }

        // A database qualifier rather than a table: suggest its tables.
        const inDatabase = catalog.tables.filter(
          (t) => t.database.toLowerCase() === qualifierName.toLowerCase(),
        );
        for (const table of inDatabase) {
          suggestions.push({
            label: table.name,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: table.name,
            range,
            detail: table.kind === 'VIEW' ? 'view' : 'table',
          });
        }
        return { suggestions };
      }

      // ── After FROM / JOIN → tables only ─────────────────────────────────
      if (TABLE_CONTEXT.test(linePrefix)) {
        for (const table of catalog.tables) {
          suggestions.push({
            label: table.qualified,
            kind: monaco.languages.CompletionItemKind.Class,
            insertText: table.qualified,
            range,
            detail: `${table.kind.toLowerCase()} · ${table.columns.length} columns`,
            filterText: `${table.name} ${table.qualified}`,
          });
        }
        return { suggestions };
      }

      // ── General context ─────────────────────────────────────────────────
      for (const keyword of catalog.keywords) {
        suggestions.push({
          label: keyword,
          kind: monaco.languages.CompletionItemKind.Keyword,
          insertText: keyword,
          range,
          sortText: `3_${keyword}`,
        });
      }

      for (const fn of catalog.functions) {
        suggestions.push({
          label: fn,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: `${fn}($0)`,
          insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
          range,
          detail: 'function',
          sortText: `4_${fn}`,
        });
      }

      // Columns of tables already mentioned rank above every other table's, so
      // writing a WHERE clause suggests the columns actually in scope first.
      const aliases = extractAliases(fullText);
      const inScope = new Set(
        aliases
          .map((a) => resolveTable(catalog, a.target)?.qualified)
          .filter((q): q is string => Boolean(q)),
      );

      for (const table of catalog.tables) {
        const scoped = inScope.has(table.qualified);
        suggestions.push({
          label: table.qualified,
          kind: monaco.languages.CompletionItemKind.Class,
          insertText: table.qualified,
          range,
          detail: table.kind.toLowerCase(),
          sortText: scoped ? `0_${table.name}` : `2_${table.name}`,
        });

        if (!scoped) continue;
        for (const column of table.columns) {
          suggestions.push({
            label: column.name,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: column.name,
            range,
            detail: `${column.dataType} · ${table.name}`,
            sortText: `1_${column.name}`,
            tags: column.restriction === 'MASKED'
              ? [monaco.languages.CompletionItemTag.Deprecated]
              : undefined,
          });
        }
      }

      return { suggestions };
    },
  });
}

/**
 * Paints policy violations and advisory warnings onto the editor.
 *
 * A violation is an error marker (red squiggle, blocks the run); a warning is
 * a warning marker (amber, informational). Both carry the rule's own reason
 * text in the hover, so the analyst reads *why* rather than just *no*.
 */
export function applyValidationMarkers(
  monaco: typeof Monaco,
  model: Monaco.editor.ITextModel,
  validation: ValidateQueryResponse | null,
): void {
  if (!validation) {
    monaco.editor.setModelMarkers(model, 'idas-policy', []);
    return;
  }

  const markers: Monaco.editor.IMarkerData[] = [];

  for (const violation of validation.violations) {
    markers.push({
      severity: monaco.MarkerSeverity.Error,
      message: formatViolation(violation),
      ...spanFor(model, violation.position, violation.subject),
      source: 'policy',
    });
  }

  for (const warning of validation.warnings) {
    markers.push({
      severity: monaco.MarkerSeverity.Warning,
      message: warning.message,
      ...spanFor(model, warning.position),
      source: 'advice',
    });
  }

  monaco.editor.setModelMarkers(model, 'idas-policy', markers);
}

function formatViolation(violation: PolicyViolation): string {
  return violation.ruleReason
    ? `${violation.message}\n\nReason: ${violation.ruleReason}`
    : violation.message;
}

/**
 * Turns a server-reported position into a marker span.
 *
 * When the server named a subject (a table or column), the span covers that
 * identifier so the squiggle sits under the offending word rather than an
 * arbitrary single character. With no position at all, the marker covers the
 * whole first line — visible, but not pretending to a precision we lack.
 */
function spanFor(
  model: Monaco.editor.ITextModel,
  position: { line: number; column: number; endLine?: number; endColumn?: number } | undefined,
  subject?: string,
): Pick<
  Monaco.editor.IMarkerData,
  'startLineNumber' | 'startColumn' | 'endLineNumber' | 'endColumn'
> {
  if (!position) {
    const firstLine = model.getLineContent(1) ?? '';
    return {
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: 1,
      endColumn: Math.max(firstLine.length + 1, 2),
    };
  }

  const identifier = subject?.split('.').pop();
  const width = identifier?.length ?? 1;

  return {
    startLineNumber: position.line,
    startColumn: position.column,
    endLineNumber: position.endLine ?? position.line,
    endColumn: position.endColumn ?? position.column + width,
  };
}

/** Editor themes matching the app's own surface colours. */
export function defineThemes(monaco: typeof Monaco): void {
  monaco.editor.defineTheme('idas-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'keyword.sql', foreground: '7dd3fc', fontStyle: 'bold' },
      { token: 'operator.sql', foreground: '9aa2b2' },
      { token: 'string.sql', foreground: 'a5d6a7' },
      { token: 'number', foreground: 'fbbf24' },
      { token: 'comment', foreground: '6a7282', fontStyle: 'italic' },
    ],
    colors: {
      'editor.background': '#0a0b0f',
      'editor.lineHighlightBackground': '#16191f',
      'editorLineNumber.foreground': '#3f4654',
      'editorLineNumber.activeForeground': '#9aa2b2',
      'editorIndentGuide.background1': '#262a33',
      'editorCursor.foreground': '#60a5fa',
    },
  });

  monaco.editor.defineTheme('idas-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'keyword.sql', foreground: '1d4ed8', fontStyle: 'bold' },
      { token: 'string.sql', foreground: '15803d' },
      { token: 'comment', foreground: '9ca3af', fontStyle: 'italic' },
    ],
    colors: {
      'editor.background': '#f3f4f6',
      'editor.lineHighlightBackground': '#e5e7eb',
    },
  });
}
